import { useCallback } from "react";
import dayjs from "dayjs";
import { fbGetContent } from "../../../../../../services/firebaseService/endPoints/admin/content";
import { toDayjs } from "../../../../../../utils/helper";
import logger from "../../../../../../utils/logger";
import { normalizeNotificationTimestamps } from "../utils/helpers";
import { toLocalFormat } from "../../../../../../schemas/event/event.transformer";
import { toast } from "react-toastify";

// The event stores date and time in one timestamp. `details.hasStartTime`
// says whether a time was actually picked. Documents saved before that flag
// existed have no flag; for those, a timestamp at exactly midnight is treated
// as "no time set".
const timeFromTimestamp = (timestamp, hasStartTime) => {
  if (!timestamp) return null;
  if (hasStartTime === false) return null;
  const value = dayjs.unix(timestamp);
  if (hasStartTime === true) return value;
  return value.hour() === 0 && value.minute() === 0 ? null : value;
};

export const useEditCopyMode = ({
  contentId,
  setValue,
  getValues,
  setOldNotificationInUtc,
  setAwayTeam,
  setMatchName,
  setMatchDate,
  setAdditionalLocations,
  handleGroupSelectionChange,
  isParent,
}) => {
  const handleEditMode = useCallback(
    async (groupId) => {
      try {
        const doc = await fbGetContent(contentId, groupId, isParent);

        if (!doc) {
          toast.error("Failed to load event data. Please try again.");
          return;
        }

        // Transform API data to local format using transformer
        const transformResult = toLocalFormat(doc, isParent);

        if (!transformResult.success) {
          toast.error("Failed to load form data. Please try again later.");
          logger.error(
            "Failed to transform content data:",
            transformResult.error,
          );
          return;
        }

        const data = transformResult.data;

        // Transform showOnHomePage dates from timestamps to dayjs array
        if (data.showOnHomePageStart && data.showOnHomePageEnd) {
          let showOnHomePageArray = [];
          const startTime = data.showOnHomePageStart * 1000;
          const endTime = data.showOnHomePageEnd * 1000;
          if (startTime > endTime) {
            showOnHomePageArray.push(toDayjs(endTime));
            showOnHomePageArray.push(toDayjs(startTime));
          } else {
            showOnHomePageArray.push(toDayjs(startTime));
            showOnHomePageArray.push(toDayjs(endTime));
          }
          data.showOnHomePage = showOnHomePageArray;
        }

        // Set form values from transformed data
        Object.keys(data).forEach((key) => {
          if (key in getValues()) {
            setValue(key, data[key]);
          }
        });

        // Set match-related fields as form values (needed for transformer in edit mode)
        if (data.matchName) {
          setValue("matchName", data.matchName);
          setMatchName(data.matchName);
        }
        if (data.isHome !== undefined) {
          setValue("isHome", data.isHome);
        }
        if (data.awayTeam) {
          setValue("awayTeam", data.awayTeam);
          setAwayTeam(data.awayTeam);
        }
        if (data.matchDate) {
          setMatchDate(data.matchDate);
        }

        // Transform eventStartDate timestamp to eventStartTime dayjs object
        setValue(
          "eventStartTime",
          timeFromTimestamp(data.eventStartDate, data.hasStartTime),
        );

        // Normalize and set notification timestamps
        const normalizedNotifications = normalizeNotificationTimestamps(
          data.notificationInUtc,
        );
        setOldNotificationInUtc(normalizedNotifications);
        setValue("notificationInUtc", normalizedNotifications);

        // Set memberResponseState based on isLinkTicket
        setValue("memberResponseState", data.isLinkTicket === true ? 2 : 1);

        if (isParent) {
          handleGroupSelectionChange(data.audience);
        }

        // Set additional locations state (managed separately from form)
        if (
          data.additionalLocations &&
          Array.isArray(data.additionalLocations)
        ) {
          // Filter out undefined/null entries and ensure at least one entry
          const validLocations = data.additionalLocations.filter(
            (loc) => loc !== null && loc !== undefined,
          );
          setAdditionalLocations(
            validLocations.length > 0 ? validLocations : [undefined],
          );
        }
      } catch (err) {
        toast.error("Failed to load event. Please try again.");
        logger.error("Failed to load content in edit mode:", err);
      }
    },
    [
      contentId,
      setValue,
      getValues,
      setOldNotificationInUtc,
      setAwayTeam,
      setMatchName,
      setMatchDate,
      setAdditionalLocations,
    ],
  );

  const handleCopyMode = useCallback(
    async (groupId) => {
      try {
        const doc = await fbGetContent(contentId, groupId);

        if (!doc) {
          toast.error("Failed to load event data. Please try again.");
          return;
        }

        // Transform API data to local format using transformer
        const transformResult = toLocalFormat(doc, isParent);

        if (!transformResult.success) {
          toast.error("Failed to load form data. Please try again later.");
          logger.error(
            "Failed to transform content data:",
            transformResult.error,
          );
          return;
        }

        const data = transformResult.data;

        // Transform showOnHomePage dates from timestamps to dayjs array
        if (data.showOnHomePageStart && data.showOnHomePageEnd) {
          let showOnHomePageArray = [];
          const startTime = data.showOnHomePageStart * 1000;
          const endTime = data.showOnHomePageEnd * 1000;
          if (startTime > endTime) {
            showOnHomePageArray.push(toDayjs(endTime));
            showOnHomePageArray.push(toDayjs(startTime));
          } else {
            showOnHomePageArray.push(toDayjs(startTime));
            showOnHomePageArray.push(toDayjs(endTime));
          }
          data.showOnHomePage = showOnHomePageArray;
        }

        // Set form values from transformed data
        Object.keys(data).forEach((key) => {
          if (key in getValues()) {
            setValue(key, data[key]);
          }
        });

        // Set match-related fields as form values (needed for display and potential editing)
        if (data.matchName) {
          setValue("matchName", data.matchName);
          setMatchName(data.matchName);
        }
        if (data.isHome !== undefined) {
          setValue("isHome", data.isHome);
        }
        if (data.awayTeam) {
          setValue("awayTeam", data.awayTeam);
          setAwayTeam(data.awayTeam);
        }
        if (data.matchDate) {
          setMatchDate(data.matchDate);
        }

        // Transform eventStartDate timestamp to eventStartTime dayjs object
        setValue(
          "eventStartTime",
          timeFromTimestamp(data.eventStartDate, data.hasStartTime),
        );

        // Normalize and set notification timestamps
        const normalizedNotifications = normalizeNotificationTimestamps(
          data.notificationInUtc,
        );
        setOldNotificationInUtc(normalizedNotifications);
        setValue("notificationInUtc", normalizedNotifications);

        // Set memberResponseState based on isLinkTicket
        setValue("memberResponseState", data.isLinkTicket === true ? 2 : 1);

        // Set additional locations state (managed separately from form)
        if (
          data.additionalLocations &&
          Array.isArray(data.additionalLocations)
        ) {
          // Filter out undefined/null entries and ensure at least one entry
          const validLocations = data.additionalLocations.filter(
            (loc) => loc !== null && loc !== undefined,
          );
          setAdditionalLocations(
            validLocations.length > 0 ? validLocations : [undefined],
          );
        }
      } catch (err) {
        toast.error("Failed to load event. Please try again.");
        logger.error("Failed to load content in copy mode:", err);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      contentId,
      setValue,
      setOldNotificationInUtc,
      getValues,
      setMatchName,
      setAwayTeam,
      setMatchDate,
      setAdditionalLocations,
    ],
  );

  const handleViewMode = useCallback(
    async (groupId) => {
      try {
        const doc = await fbGetContent(contentId, groupId);

        if (!doc) {
          toast.error("Failed to load event data. Please try again.");
          return;
        }

        // Transform API data to local format using transformer
        const transformResult = toLocalFormat(doc, isParent);

        if (!transformResult.success) {
          toast.error("Failed to load form data. Please try again later.");
          logger.error(
            "Failed to transform content data:",
            transformResult.error,
          );
          return;
        }

        const data = transformResult.data;

        // Transform showOnHomePage dates from timestamps to dayjs array
        if (data.showOnHomePageStart && data.showOnHomePageEnd) {
          let showOnHomePageArray = [];
          const startTime = data.showOnHomePageStart * 1000;
          const endTime = data.showOnHomePageEnd * 1000;
          if (startTime > endTime) {
            showOnHomePageArray.push(toDayjs(endTime));
            showOnHomePageArray.push(toDayjs(startTime));
          } else {
            showOnHomePageArray.push(toDayjs(startTime));
            showOnHomePageArray.push(toDayjs(endTime));
          }
          data.showOnHomePage = showOnHomePageArray;
        }

        // Set form values from transformed data
        Object.keys(data).forEach((key) => {
          if (key in getValues()) {
            setValue(key, data[key]);
          }
        });

        // Set match-related fields as form values (needed for transformer in view mode)
        if (data.matchName) {
          setValue("matchName", data.matchName);
          setMatchName(data.matchName);
        }
        if (data.isHome !== undefined) {
          setValue("isHome", data.isHome);
        }
        if (data.awayTeam) {
          setValue("awayTeam", data.awayTeam);
          setAwayTeam(data.awayTeam);
        }
        if (data.matchDate) {
          setMatchDate(data.matchDate);
        }

        // Transform eventStartDate timestamp to eventStartTime dayjs object
        setValue(
          "eventStartTime",
          timeFromTimestamp(data.eventStartDate, data.hasStartTime),
        );

        // Normalize and set notification timestamps
        const normalizedNotifications = normalizeNotificationTimestamps(
          data.notificationInUtc,
        );
        setOldNotificationInUtc(normalizedNotifications);
        setValue("notificationInUtc", normalizedNotifications);

        // Set memberResponseState based on isLinkTicket
        setValue("memberResponseState", data.isLinkTicket === true ? 2 : 1);

        // Set additional locations state (managed separately from form)
        if (
          data.additionalLocations &&
          Array.isArray(data.additionalLocations)
        ) {
          // Filter out undefined/null entries and ensure at least one entry
          const validLocations = data.additionalLocations.filter(
            (loc) => loc !== null && loc !== undefined,
          );
          setAdditionalLocations(
            validLocations.length > 0 ? validLocations : [undefined],
          );
        }
      } catch (err) {
        toast.error("Failed to load event. Please try again.");
        logger.error("Failed to load content in view mode:", err);
      }
    },
    [
      contentId,
      setValue,
      getValues,
      setOldNotificationInUtc,
      setAwayTeam,
      setMatchName,
      setMatchDate,
      setAdditionalLocations,
    ],
  );

  return { handleEditMode, handleCopyMode, handleViewMode };
};
